import { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { X } from "lucide-react";
import { RETENTION_LABEL } from "@/config/retention";

const DISMISSED_KEY = "ct_retention_banner_dismissed";

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Signed-in notice that files are deleted after the retention window. Its
 * height is published as `--retention-h` so `--navbar-h` (and everything
 * padded by it) grows with the strip, which wraps to two lines on phones.
 */
export function RetentionBanner() {
  const { isSignedIn } = useAuth();
  // Read synchronously on first render so a dismissed banner never flashes in.
  const [dismissed, setDismissed] = useState(readDismissed);
  const ref = useRef<HTMLDivElement>(null);
  const visible = isSignedIn === true && !dismissed;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!visible || !el) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty("--retention-h", `${el.offsetHeight}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--retention-h");
    };
  }, [visible]);

  if (!visible) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      // Private mode: hidden for this visit only.
    }
  };

  return (
    <div
      ref={ref}
      className="relative bg-[#f3f0fb] border-b border-[#241d3a]/10 py-1.5 px-9 text-center text-[11px] md:text-[12px] font-semibold leading-snug text-[#241d3a]/60 text-balance"
    >
      Your uploads and videos are deleted after {RETENTION_LABEL}.{" "}
      <Link to="/assets" className="text-[#241d3a] underline underline-offset-2 hover:text-[#7c5cf0] transition-colors">
        Save your favourites from the library.
      </Link>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-[#241d3a]/50 hover:text-[#241d3a] hover:bg-[#241d3a]/[0.06] transition-colors"
      >
        <X className="size-3.5" strokeWidth={2.5} />
      </button>
    </div>
  );
}
